import { DateTime } from "luxon";
import { useMemo } from "react";
import { useDayStatsInputs } from "@/hooks/useDayStatsInputs";
import { useScheduleCacheEnabled, useScheduleStartDay } from "@/hooks/useScheduleWeekRules";
import { useShownWeek } from "@/hooks/useShownWeek";
import { getVisibleWeekOffset } from "@/utils/dateUtils";
import { scheduledShiftsByIdOf } from "@/utils/departmentWeeks";
import {
	DAYS_PER_WEEK,
	type DepartmentWageDataResponse,
	type Nurse,
	type NurseInShift,
	type ShiftPerson,
	type SpecificPreference,
	type WeekData,
} from "@/utils/models";
import { weekIndexOfDate } from "@/utils/panelWeeks";
import { useHospitalData } from "../HospitalDataProvider";
import { useWeekIndex } from "../WeekIndexProvider";

export type WeekDays = {
	/** Weeks around the shown one, in the old list's shape, as `useScheduleWeeks` reads them. */
	weeks: WeekData[];
	/** The shown Sunday week's position in `weeks`; a week starting later than Sunday spills into the next one. */
	weekIndex: number;
	/** Whole nurses (time off, wages, punches): with the cache on, no one until the roster loads. */
	nurses: Nurse[];
	getNurseById: (id: number) => Nurse | undefined;
	/** The people the weeks' shifts name, as `useScheduleWeeks` gives them: there as soon as the weeks are. */
	people: ShiftPerson[];
	getPerson: (id: number) => ShiftPerson | undefined;
	/** The weeks' time off as the schedule returns it, read until the roster loads; empty with the cache off. */
	timeOff: SpecificPreference[];
	wageData: DepartmentWageDataResponse | null;
	scheduledShiftsById: Map<string, NurseInShift>;
	/** The first and last day of the schedule, for the date picker; undefined while unknown. */
	firstDate: DateTime | undefined;
	lastDate: DateTime | undefined;
	/** With the cache on: the weeks around the shown one or the roster is loading, or failed to; `refetch` asks again. Always settled with it off. */
	isLoading: boolean;
	isError: boolean;
	/**
	 * The shown week's days, which the Week grid waits for: the weeks either side fill in after it. Failed when any of
	 * the weeks did, as the checks across the week's edges read them.
	 */
	isScheduleLoading: boolean;
	isScheduleError: boolean;
	/** The weeks either side are still loading: what a check across the week's edges waits for. Always false with the cache off. */
	isWindowLoading: boolean;
	refetch: () => void;
};

// One object each, so a week that isn't in yet doesn't hand out new arrays on every render.
const NO_WEEKS: WeekData[] = [];
const NO_NURSES: Nurse[] = [];
const NO_PEOPLE: ShiftPerson[] = [];
const NO_TIME_OFF: SpecificPreference[] = [];
const NO_SHIFTS = new Map<string, NurseInShift>();
const noNurse = (): Nurse | undefined => undefined;
const noPerson = (): ShiftPerson | undefined => undefined;
// The old list loads and reloads itself, so with the cache off there is nothing to ask again.
const noRefetch = (): void => undefined;

/**
 * The week the staffing pages show: `useDayStatsInputs` around the shown week (`useShownWeek`), the weeks with the
 * wages and people the day stats need, plus shifts by id. With `staffing_schedule_cache` on they come from the cache
 * and the roster (a draft's edits too: they patch the cached day); off, from the old list exactly as before.
 */
export const useWeekDays = (): WeekDays => {
	const { weekIndex } = useWeekIndex();
	const { scheduledShiftsById } = useHospitalData();
	const isCacheEnabled = useScheduleCacheEnabled();
	const startDay = useScheduleStartDay();
	const { weekStart } = useShownWeek();

	// DEFAULT: before any week is known nothing is built (see below); the cache meanwhile asks for today's week, where the Week view opens.
	const schedule = useDayStatsInputs(weekStart ?? DateTime.now());
	const cachedShiftsById = useMemo(() => scheduledShiftsByIdOf(schedule.weeks), [schedule.weeks]);
	const bounds = { firstDate: schedule.firstDate, lastDate: schedule.lastDate };

	if (!isCacheEnabled) {
		return {
			weeks: schedule.weeks,
			weekIndex,
			nurses: schedule.nurses,
			getNurseById: schedule.getNurseById,
			people: schedule.people,
			getPerson: schedule.getPerson,
			timeOff: schedule.timeOff,
			wageData: schedule.wageData,
			scheduledShiftsById,
			...bounds,
			isLoading: false,
			isError: false,
			isScheduleLoading: false,
			isScheduleError: false,
			isWindowLoading: false,
			refetch: noRefetch,
		};
	}
	// The grid draws the shown Sunday week and, when the view starts later in the week, that many days of the next.
	const isShownWeekLoading =
		weekStart === undefined || schedule.isLoadingBetween(weekStart, weekStart.plus({ days: DAYS_PER_WEEK - 1 + getVisibleWeekOffset(startDay) }));
	// No week named yet means the old list, which names the opening week, is still loading.
	const loadState = {
		...bounds,
		isLoading: schedule.isLoading || weekStart === undefined,
		isError: schedule.isError,
		isScheduleLoading: isShownWeekLoading,
		isScheduleError: schedule.isScheduleError,
		isWindowLoading: schedule.isScheduleLoading || weekStart === undefined,
		refetch: schedule.refetch,
	};
	// The schedule's own people name the shifts, so the weeks don't wait for the roster.
	if (weekStart === undefined || isShownWeekLoading || schedule.isScheduleError) {
		return {
			weeks: NO_WEEKS,
			weekIndex: 0,
			nurses: NO_NURSES,
			getNurseById: noNurse,
			people: NO_PEOPLE,
			getPerson: noPerson,
			timeOff: NO_TIME_OFF,
			wageData: null,
			scheduledShiftsById: NO_SHIFTS,
			...loadState,
		};
	}
	return {
		weeks: schedule.weeks,
		weekIndex: weekIndexOfDate(schedule.weeks, weekStart),
		nurses: schedule.nurses,
		getNurseById: schedule.getNurseById,
		people: schedule.people,
		getPerson: schedule.getPerson,
		timeOff: schedule.timeOff,
		wageData: schedule.wageData,
		scheduledShiftsById: cachedShiftsById,
		...loadState,
	};
};
